import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProductSpec } from "@/data/phoneSpecs";
import { Star, Trophy } from "lucide-react";
import { useBackButton } from "@/hooks/useBackButton";

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface PhoneComparisonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: ProductSpec[];
}

const specLabels: { key: keyof ProductSpec["specs"]; label: string }[] = [
  { key: "screen", label: "Tela" },
  { key: "processor", label: "Processador" },
  { key: "ram", label: "RAM" },
  { key: "storage", label: "Armazenamento" },
  { key: "camera", label: "Câmera Traseira" },
  { key: "frontCamera", label: "Câmera Frontal" },
  { key: "battery", label: "Bateria" },
  { key: "os", label: "Sistema" },
  { key: "connectivity", label: "Conectividade" },
  { key: "weight", label: "Peso" },
];

const ratingLabels: { key: keyof ProductSpec["ratings"]; label: string }[] = [
  { key: "screen", label: "Tela" },
  { key: "performance", label: "Performance" },
  { key: "camera", label: "Câmera" },
  { key: "battery", label: "Bateria" },
  { key: "value", label: "Custo-benefício" },
  { key: "overall", label: "Geral" },
];

const PhoneComparison = ({ open, onOpenChange, products }: PhoneComparisonProps) => {
  const [selected, setSelected] = useState<string[]>([]);
  const closeComparison = useCallback(() => onOpenChange(false), [onOpenChange]);
  useBackButton(open, closeComparison, "phone-comparison");

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const phone1 = products.find((p) => p.id === selected[0]);
  const phone2 = products.find((p) => p.id === selected[1]);

  const getCellClass = (val1: number, val2: number, isFirst: boolean) => {
    if (val1 === val2) return "";
    return (isFirst ? val1 > val2 : val2 > val1) ? "text-primary font-bold" : "text-muted-foreground";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-foreground flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Comparar Aparelhos
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">Selecione 2 aparelhos para comparar:</p>
        <div className="grid grid-cols-2 gap-3">
          {products.map((p) => (
            <button
              key={p.id}
              onClick={() => toggleSelect(p.id)}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                selected.includes(p.id)
                  ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                  : "border-border bg-secondary hover:border-primary/50"
              }`}
            >
              <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover" />
              <div className="text-left flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.color}</p>
                <p className="text-xs font-bold text-gradient-gold">{p.price}</p>
              </div>
              {selected.includes(p.id) && (
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-primary-foreground">{selected.indexOf(p.id) + 1}</span>
                </div>
              )}
            </button>
          ))}
        </div>

        {phone1 && phone2 && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-2 items-center">
              <div />
              <div className="text-center">
                <img src={phone1.image} alt={phone1.name} className="w-16 h-16 rounded-lg object-cover mx-auto mb-1" />
                <p className="text-xs font-semibold text-foreground truncate">{phone1.name}</p>
              </div>
              <div className="text-center">
                <img src={phone2.image} alt={phone2.name} className="w-16 h-16 rounded-lg object-cover mx-auto mb-1" />
                <p className="text-xs font-semibold text-foreground truncate">{phone2.name}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 items-center bg-secondary/50 rounded-lg px-3 py-2">
              <span className="text-xs font-semibold text-foreground">Preço</span>
              <span className={`text-xs text-center ${phone1.priceNum <= phone2.priceNum ? "text-primary font-bold" : "text-muted-foreground"}`}>
                {phone1.price}
              </span>
              <span className={`text-xs text-center ${phone2.priceNum <= phone1.priceNum ? "text-primary font-bold" : "text-muted-foreground"}`}>
                {phone2.price}
              </span>
            </div>

            <div>
              <h4 className="text-sm font-display font-semibold text-foreground mb-2">Especificações</h4>
              <div className="space-y-0">
                {specLabels.map(({ key, label }, i) => (
                  <div key={key} className={`grid grid-cols-3 gap-2 items-center px-3 py-2 ${i % 2 === 0 ? "bg-secondary/30" : ""} rounded`}>
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <span className="text-xs text-center text-foreground">{phone1.specs[key]}</span>
                    <span className="text-xs text-center text-foreground">{phone2.specs[key]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-display font-semibold text-foreground mb-2">Notas (0 a 10)</h4>
              <div className="space-y-0">
                {ratingLabels.map(({ key, label }, i) => (
                  <div key={key} className={`grid grid-cols-3 gap-2 items-center px-3 py-2 ${i % 2 === 0 ? "bg-secondary/30" : ""} rounded`}>
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <span className={`text-xs text-center ${getCellClass(phone1.ratings[key], phone2.ratings[key], true)}`}>
                      <Star className="w-3 h-3 inline mr-0.5 text-primary" />
                      {phone1.ratings[key].toFixed(1)}
                    </span>
                    <span className={`text-xs text-center ${getCellClass(phone1.ratings[key], phone2.ratings[key], false)}`}>
                      <Star className="w-3 h-3 inline mr-0.5 text-primary" />
                      {phone2.ratings[key].toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {phone1.ratings.overall !== phone2.ratings.overall && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-center">
                <p className="text-sm font-semibold text-foreground flex items-center justify-center gap-2">
                  <Trophy className="w-4 h-4 text-primary" />
                  Melhor avaliado:{" "}
                  <span className="text-primary">
                    {phone1.ratings.overall > phone2.ratings.overall ? phone1.name : phone2.name}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneComparison;
